import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Package, Download, FileText, ShoppingCart, Music2,
  RefreshCw, Truck, CheckCircle2, Clock, Loader2,
  ExternalLink, ChevronDown, ChevronUp, Zap, MapPin
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Status config for the neon Tokyo timeline ──────────────
const MERCH_STEPS = [
  { key: "placed",        label: "Order Placed",        icon: ShoppingCart },
  { key: "paid",          label: "Payment Confirmed",   icon: CheckCircle2 },
  { key: "printify",      label: "Sent to Printify",    icon: Package },
  { key: "in_production", label: "In Production",       icon: Zap },
  { key: "shipped",       label: "Shipped",             icon: Truck },
  { key: "delivered",     label: "Delivered",           icon: MapPin },
];

const BEAT_STEPS = [
  { key: "placed",    label: "Order Placed",       icon: ShoppingCart },
  { key: "paid",      label: "Payment Confirmed",  icon: CheckCircle2 },
  { key: "download",  label: "Ready to Download",  icon: Download },
];

function getActiveStep(order) {
  const hasMerch = order.items?.some(i => i.item_type === "merch");
  if (!hasMerch) {
    // Beat-only: step 2 = "Ready to Download" — mark as done (idx 2) when completed
    return order.status === "completed" ? 3 : 1;
  }
  const fs = order.fulfillment_status || "";
  const tracking = order.tracking_info;
  if (tracking?.delivered_at || order.status === "delivered") return 5;
  if (tracking?.shipped_at || fs === "shipped" || order.status === "shipped") return 4;
  if (fs === "in_production" || order.printify_status === "sent-to-production") return 3;
  if (order.printify_order_id || fs === "submitted_to_printify") return 2;
  if (order.status === "completed") return 1;
  return 1;
}

function StatusTimeline({ order }) {
  const hasMerch = order.items?.some(i => i.item_type === "merch");
  const steps = hasMerch ? MERCH_STEPS : BEAT_STEPS;
  const activeIdx = getActiveStep(order);

  return (
    <div className="relative pl-8 space-y-0" data-testid="order-timeline">
      {/* Vertical line */}
      <div className="absolute left-[14px] top-4 bottom-4 w-px bg-zinc-800" />

      {steps.map((step, idx) => {
        const Icon = step.icon;
        const done = idx < activeIdx;
        const active = idx === activeIdx;
        const pending = idx > activeIdx;

        return (
          <div key={step.key} className="relative flex items-start gap-4 pb-6 last:pb-0" data-testid={`step-${step.key}`}>
            {/* Node */}
            <div className={`
              absolute -left-8 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center border-2 z-10 transition-all
              ${done   ? "bg-neon-cyan/20 border-neon-cyan shadow-[0_0_12px_rgba(0,255,255,0.5)]" : ""}
              ${active ? "bg-neon-magenta/20 border-neon-magenta shadow-[0_0_16px_rgba(255,0,200,0.7)] animate-pulse" : ""}
              ${pending ? "bg-zinc-900 border-zinc-700" : ""}
            `}>
              <Icon className={`w-3.5 h-3.5
                ${done ? "text-neon-cyan" : ""}
                ${active ? "text-neon-magenta" : ""}
                ${pending ? "text-zinc-600" : ""}
              `} />
            </div>

            {/* Content */}
            <div className={`ml-1 transition-opacity ${pending ? "opacity-40" : "opacity-100"}`}>
              <p className={`text-xs font-heading uppercase tracking-widest
                ${done ? "text-neon-cyan" : ""}
                ${active ? "text-neon-magenta" : ""}
                ${pending ? "text-zinc-600" : ""}
              `}>
                {step.label}
                {active && <span className="ml-2 text-neon-magenta/60">— In Progress</span>}
                {done && idx === 0 && order.created_at &&
                  <span className="ml-2 text-zinc-500 font-normal normal-case">
                    {new Date(order.created_at).toLocaleDateString()}
                  </span>
                }
              </p>

              {/* Tracking detail on shipped step */}
              {step.key === "shipped" && (done || active) && order.tracking_info?.tracking_number && (
                <div className="mt-1.5 p-2 bg-zinc-900 border border-neon-cyan/20 inline-flex flex-col gap-0.5">
                  <span className="font-mono text-xs text-white">{order.tracking_info.carrier} · {order.tracking_info.tracking_number}</span>
                  {order.tracking_info.tracking_url && (
                    <a href={order.tracking_info.tracking_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-neon-cyan hover:text-white transition-colors flex items-center gap-1 mt-0.5">
                      Track Package <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OrderCard({ order, onRefreshTracking }) {
  const [expanded, setExpanded] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasMerch = order.items?.some(i => i.item_type === "merch");
  const navigate = useNavigate();

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefreshTracking(order.order_id);
      toast.success("Tracking updated!");
    } catch {
      toast.error("Could not refresh tracking");
    } finally {
      setRefreshing(false);
    }
  };

  const handleDownloadLicense = () => {
    const token = localStorage.getItem("token");
    window.open(`${API}/license/generate/${order.order_id}?token=${token}`, "_blank");
  };

  const statusLabel = (() => {
    if (order.tracking_info?.delivered_at) return { label: "Delivered", color: "bg-neon-green/20 text-neon-green border-neon-green/30" };
    if (order.tracking_info?.tracking_number) return { label: "Shipped", color: "bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30" };
    if (order.fulfillment_status === "in_production" || order.printify_status === "sent-to-production")
      return { label: "In Production", color: "bg-neon-magenta/20 text-neon-magenta border-neon-magenta/30" };
    if (order.printify_order_id) return { label: "At Printify", color: "bg-neon-yellow/20 text-neon-yellow border-neon-yellow/30" };
    if (order.status === "completed") return { label: "Completed", color: "bg-neon-green/20 text-neon-green border-neon-green/30" };
    return { label: order.status || "Processing", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" };
  })();

  return (
    <div
      className="border border-white/10 bg-zinc-950 overflow-hidden transition-all hover:border-white/20"
      data-testid={`order-${order.order_id}`}
    >
      {/* Header row */}
      <div
        className="flex items-center justify-between p-5 cursor-pointer select-none"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex flex-col items-center justify-center w-12 h-12 border border-white/10 bg-zinc-900">
            <Package className="w-5 h-5 text-zinc-400" />
          </div>
          <div>
            <p className="font-mono text-xs text-zinc-500 mb-0.5">{order.order_id}</p>
            <p className="text-sm text-white font-medium">
              {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? "s" : ""}
              <span className="text-zinc-500 font-normal"> · </span>
              <span className="text-neon-cyan font-heading">${order.total?.toFixed(2)}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`text-xs border ${statusLabel.color}`}>{statusLabel.label}</Badge>
          <span className="text-xs text-zinc-600 hidden sm:block">
            {new Date(order.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/5 p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Timeline */}
          <div>
            <p className="text-xs font-heading uppercase tracking-widest text-zinc-600 mb-4">Status Timeline</p>
            <StatusTimeline order={order} />
          </div>

          {/* Right: Items + actions */}
          <div className="space-y-4">
            <p className="text-xs font-heading uppercase tracking-widest text-zinc-600">Items</p>
            <div className="space-y-2">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-zinc-900/70 border border-white/5">
                  {item.image
                    ? <img src={item.image} alt={item.name} className="w-12 h-12 object-cover flex-shrink-0" />
                    : <div className="w-12 h-12 bg-zinc-800 flex items-center justify-center flex-shrink-0">
                        {item.item_type === "beat" ? <Music2 className="w-5 h-5 text-zinc-600" /> : <Package className="w-5 h-5 text-zinc-600" />}
                      </div>
                  }
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.name || item.item_id}</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400 capitalize">{item.item_type}</Badge>
                      {item.license_type && <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">{item.license_type} license</Badge>}
                      {item.size && <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">{item.size}</Badge>}
                      {item.color && <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">{item.color}</Badge>}
                    </div>
                  </div>
                  <span className="text-xs text-zinc-500 flex-shrink-0">×{item.quantity || 1}</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              {hasMerch && order.printify_order_id && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-neon-cyan/40 text-neon-cyan hover:bg-neon-cyan/10 text-xs"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  data-testid={`refresh-tracking-${order.order_id}`}
                >
                  {refreshing
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                    : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  }
                  Refresh Tracking
                </Button>
              )}
              {order.status === "completed" && order.items?.some(i => i.item_type === "beat") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-neon-green/40 text-neon-green hover:bg-neon-green/10 text-xs"
                  onClick={handleDownloadLicense}
                  data-testid={`download-license-${order.order_id}`}
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" />
                  License PDF
                </Button>
              )}
              {order.status === "completed" && order.items?.some(i => i.item_type === "beat") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-800 text-xs"
                  onClick={() => navigate("/orders?tab=downloads")}
                  data-testid={`go-downloads-${order.order_id}`}
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  My Downloads
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────
const OrdersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("orders");

  const getHeaders = () => {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchData = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const [ordersRes, dlRes] = await Promise.all([
        axios.get(`${API}/my-orders`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/my-downloads`, { headers: getHeaders(), withCredentials: true }),
      ]);
      setOrders(ordersRes.data || []);
      setDownloads(dlRes.data || []);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Read ?tab= from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "downloads") setActiveTab("downloads");
  }, []);

  const handleRefreshTracking = async (orderId) => {
    const res = await axios.get(`${API}/orders/${orderId}/track`, { headers: getHeaders(), withCredentials: true });
    setOrders(prev => prev.map(o => o.order_id === orderId ? res.data : o));
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="orders-page">
        <div className="text-center">
          <Package className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
          <h2 className="font-heading text-2xl uppercase mb-2">Sign In to View Orders</h2>
          <p className="text-zinc-500 text-sm mb-6">Your order history lives here</p>
          <Button onClick={() => navigate("/")} className="bg-neon-cyan text-black font-bold uppercase">Go Home</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4 sm:px-6" data-testid="orders-page">
      <div className="max-w-5xl mx-auto">

        {/* Page header */}
        <div className="mb-10">
          <p className="text-xs font-heading uppercase tracking-widest text-neon-cyan mb-2">Account</p>
          <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight">My Orders</h1>
          <p className="text-zinc-500 text-sm mt-2">{orders.length} order{orders.length !== 1 ? "s" : ""} total</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-white/10">
          {[
            { id: "orders", label: `Orders (${orders.length})`, testid: "orders-tab" },
            { id: "downloads", label: `Downloads (${downloads.length})`, testid: "downloads-tab" },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-heading uppercase tracking-widest transition-all border-b-2 -mb-px ${
                activeTab === tab.id
                  ? "text-neon-cyan border-neon-cyan"
                  : "text-zinc-600 border-transparent hover:text-zinc-300"
              }`}
              data-testid={tab.testid}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Orders tab */}
        {activeTab === "orders" && (
          <>
            {orders.length === 0 ? (
              <div className="text-center py-24">
                <ShoppingCart className="w-16 h-16 text-zinc-800 mx-auto mb-4" />
                <p className="text-zinc-500 mb-6">No orders yet — let's change that</p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <Button onClick={() => navigate("/beats")} className="bg-neon-cyan text-black font-bold uppercase">Browse Beats</Button>
                  <Button onClick={() => navigate("/merch")} variant="outline" className="border-white/20 text-zinc-300 uppercase font-bold">Shop Merch</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <OrderCard
                    key={order.order_id}
                    order={order}
                    onRefreshTracking={handleRefreshTracking}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Downloads tab */}
        {activeTab === "downloads" && (
          <>
            {downloads.length === 0 ? (
              <div className="text-center py-24">
                <Download className="w-16 h-16 text-zinc-800 mx-auto mb-4" />
                <p className="text-zinc-500 mb-6">No downloads yet</p>
                <p className="text-xs text-zinc-600">Purchase beats to access them here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {downloads.map((dl, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 border border-white/10 bg-zinc-950" data-testid={`download-${dl.beat_id}`}>
                    {dl.image_url
                      ? <img src={dl.image_url} alt={dl.title} className="w-14 h-14 object-cover flex-shrink-0" />
                      : <div className="w-14 h-14 bg-zinc-900 flex items-center justify-center"><Music2 className="w-5 h-5 text-zinc-600" /></div>
                    }
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{dl.title}</p>
                      <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400 mt-1">{dl.license_type} License</Badge>
                    </div>
                    {dl.audio_url && (
                      <a href={dl.audio_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="border-neon-green/50 text-neon-green hover:bg-neon-green/10 text-xs uppercase font-bold">
                          <Download className="w-3.5 h-3.5 mr-1.5" />Download
                        </Button>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
